import { TrendingUp, BarChart3, FileText, Activity } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'

export default function AnalyticsSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        Numbers tell the story of your sales performance. The Analytics and Reports modules help you
        understand what is working, where deals are stalling, and how to focus your energy for the
        best results.
      </p>

      <section id="analytics-overview" aria-labelledby="analytics-overview-heading">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="h-4 w-4" aria-hidden="true" />
          <h3 id="analytics-overview-heading" className="font-semibold">
            Analytics Dashboard
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Navigate to <span className="font-medium">Analytics</span> in the sidebar to see your
          sales performance at a glance:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Key Metrics</span> — Open opportunities count, total
            pipeline value, and win rate at a glance.
          </li>
          <li>
            <span className="font-medium">Revenue Trends</span> — Monthly Closed Won revenue over
            time. Spot growth patterns and seasonal trends.
          </li>
          <li>
            <span className="font-medium">Pipeline Funnel</span> — Visual breakdown of deals by
            stage, showing where opportunities are concentrated in your pipeline.
          </li>
          <li>
            <span className="font-medium">Days to Close</span> — Average time to close deals,
            helping you understand and optimize your sales cycle length.
          </li>
          <li>
            <span className="font-medium">Sales Forecast</span> — Pipeline projections based on deal
            amounts and stage probabilities. Helpful for planning and goal setting.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          Use the <span className="font-medium">date range filters</span> at the top to focus on
          specific periods. Quick presets include Last 30 Days, This Quarter, Year to Date, and Last
          12 Months. <span className="font-medium">Year to Date / This Year</span> uses Grackle's
          fiscal year, which runs{' '}
          <span className="font-medium">March 1 to the end of February</span> — not the
          January-to-December calendar year — so these figures line up with how the business reports
          performance.
        </p>
      </section>
      <Separator />

      <section id="reports" aria-labelledby="reports-heading">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="h-4 w-4" aria-hidden="true" />
          <h3 id="reports-heading" className="font-semibold">
            Pre-Built Reports
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Go to <span className="font-medium">Reports</span> in the sidebar for ready-made reports
          that answer common sales questions:
        </p>
        <ul
          className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Pipeline Report</span> — How many deals are at each stage?
            Where are the bottlenecks?
          </li>
          <li>
            <span className="font-medium">Days to Close</span> — How long does it take to close
            deals on average? Are certain deal types faster than others?
          </li>
          <li>
            <span className="font-medium">Win/Loss Analysis</span> — What is your win rate? Why are
            deals being lost?
          </li>
          <li>
            <span className="font-medium">Top Accounts</span> — Which customers generate the most
            revenue?
          </li>
          <li>
            <span className="font-medium">Big Opportunities</span> — Which deals in your pipeline
            have the highest value?
          </li>
        </ul>
      </section>
      <Separator />

      <section id="report-builder" aria-labelledby="report-builder-heading">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-4 w-4" aria-hidden="true" />
          <h3 id="report-builder-heading" className="font-semibold">
            Custom Report Builder
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Need something the pre-built reports do not cover? Build your own:
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Go to <span className="font-medium">Reports</span> and click{' '}
            <span className="font-medium">Create Report</span>
          </li>
          <li>
            Choose a data source (Accounts, Contacts, Leads, Opportunities, etc.) or use a Quick
            Start template
          </li>
          <li>Select the fields you want to see as columns</li>
          <li>Add filters to narrow the data (e.g., only Closed Won deals, only leads from Q4)</li>
          <li>Choose a chart type — table, bar chart, line chart, or pie chart</li>
          <li>
            Set a date range using presets (Today, This Week, This Month, This Quarter) or pick
            custom dates
          </li>
          <li>The preview updates live as you build — no need to run the report separately</li>
          <li>Save your report for reuse, or export to CSV or PDF</li>
        </ol>
      </section>
      <Separator />

      <section id="deal-health" aria-labelledby="deal-health-heading">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-4 w-4" aria-hidden="true" />
          <h3 id="deal-health-heading" className="font-semibold">
            Deal Health Score
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Every open opportunity gets a predictive health score from 0 to 100 that flags at-risk
          deals before they stall. The score appears as a color-coded badge in the{' '}
          <span className="font-medium">Health</span> column on the Opportunities list, and as a
          detailed breakdown card in the Key Metrics section on the Opportunity detail page. Closed
          opportunities do not show a health score.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Score ranges:</span>
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-3"
          role="list"
        >
          <li>
            <span className="font-medium">80-100 Healthy</span> (green) — Deal is on track with
            strong engagement.
          </li>
          <li>
            <span className="font-medium">60-79 Good</span> (blue) — Deal is progressing well, minor
            areas to watch.
          </li>
          <li>
            <span className="font-medium">40-59 Needs Attention</span> (yellow) — Some signals are
            weak. Review and take action.
          </li>
          <li>
            <span className="font-medium">20-39 At Risk</span> (orange) — Multiple warning signs.
            Prioritize this deal.
          </li>
          <li>
            <span className="font-medium">0-19 Critical</span> (red) — Deal is likely stalled.
            Immediate action needed.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">How it is calculated:</span> The score combines four equally
          weighted signals (25 points each):
        </p>
        <ul
          className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            <span className="font-medium">Activity Recency (0-25)</span> — When was the last task or
            event on this deal? Activity within 7 days earns full points. No activity in over 60
            days scores zero.
          </li>
          <li>
            <span className="font-medium">Sales Process (0-25)</span> — Have you completed the key
            milestones? Budget Confirmed, Discovery Completed, and ROI Analysis Completed each
            contribute points.
          </li>
          <li>
            <span className="font-medium">Close Date Health (0-25)</span> — Is the close date
            realistic? Deals with healthy runway earn full points. Overdue deals score progressively
            lower the longer they are past due.
          </li>
          <li>
            <span className="font-medium">Stage Progression (0-25)</span> — Based on the deal's
            probability percentage. Higher probability means greater confidence the deal will close.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground">
          On the detail page, the Deal Health card shows each signal's individual score so you can
          see exactly which area needs improvement. Hover over a score to see a detailed
          explanation.
        </p>
      </section>
      <Separator />

      <section id="lead-scoring" aria-labelledby="lead-scoring-heading">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-4 w-4" aria-hidden="true" />
          <h3 id="lead-scoring-heading" className="font-semibold">
            Lead Scoring
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Every active lead (New, Working, or Qualified) gets a predictive score from 0 to 100 that
          helps you prioritize which leads to work first. The score appears as a color-coded badge
          in the <span className="font-medium">Score</span> column on the Leads list, and as a
          detailed breakdown card on the Lead detail page. Disqualified leads do not show a score.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Score ranges:</span>
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-3"
          role="list"
        >
          <li>
            <span className="font-medium">80-100 Hot Lead</span> (green) — High-priority lead with
            strong signals.
          </li>
          <li>
            <span className="font-medium">60-79 Warm Lead</span> (blue) — Good prospect, keep
            engaging.
          </li>
          <li>
            <span className="font-medium">40-59 Needs Work</span> (yellow) — Some signals are weak.
            Invest more effort.
          </li>
          <li>
            <span className="font-medium">20-39 Cold Lead</span> (orange) — Multiple weak signals.
            Consider re-qualifying.
          </li>
          <li>
            <span className="font-medium">0-19 Low Priority</span> (red) — Likely stale or
            incomplete. Review before spending time.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">How it is calculated:</span> The score combines four equally
          weighted signals (25 points each):
        </p>
        <ul
          className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            <span className="font-medium">Engagement (0-25)</span> — When was the last task or event
            on this lead? Activity within 7 days earns full points. No activity in over 60 days
            scores zero.
          </li>
          <li>
            <span className="font-medium">Profile Completeness (0-25)</span> — How much do we know
            about this lead? Email, phone, title, company, industry, website, and company size each
            contribute points.
          </li>
          <li>
            <span className="font-medium">Lead Source Quality (0-25)</span> — Sources are tiered by
            historical quality. Referrals and RFP responses score highest; existing customer
            re-inquiries score lower.
          </li>
          <li>
            <span className="font-medium">Status and Timeliness (0-25)</span> — Qualified leads
            score higher than New. Fresh leads get a bonus; leads over 6 months old get a penalty
            for staleness.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground">
          On the detail page, the Lead Score card shows each signal's individual score so you can
          see exactly where to focus your effort. Hover over a score to see a detailed explanation.
        </p>
      </section>
      <Separator />

      <section id="stage-velocity" aria-labelledby="stage-velocity-heading">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-4 w-4" aria-hidden="true" />
          <h3 id="stage-velocity-heading" className="font-semibold">
            Stage Velocity Tracker
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Every open opportunity shows how long the deal has been in its current stage compared to
          the historical average for won deals. The indicator appears just below the stage progress
          bar on the Opportunity detail page.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">How to read it:</span>
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            <span className="font-medium">Green dot</span> — Deal is moving faster than average for
            this stage.
          </li>
          <li>
            <span className="font-medium">Yellow dot</span> — Deal is taking 1-2x the average. Keep
            an eye on it.
          </li>
          <li>
            <span className="font-medium">Red dot</span> — Deal has been in this stage 2x+ longer
            than average. Take action.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground">
          Click the indicator to see a popup with average durations for every stage, based on your
          historical won deals.
        </p>
      </section>
      <Separator />

      <section id="win-probability" aria-labelledby="win-probability-heading">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-4 w-4" aria-hidden="true" />
          <h3 id="win-probability-heading" className="font-semibold">
            Win Probability
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Every open opportunity gets a win probability percentage (5-95%) that predicts how likely
          the deal is to close. It complements the Deal Health Score — health measures whether the
          deal is being worked well, while win probability predicts whether it will actually close.
          A color-coded badge appears in the <span className="font-medium">Win %</span> column on
          the Opportunities list, and a detailed breakdown card shows on the detail page.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">How it is calculated:</span> The probability combines four
          equally weighted signals (25 points each):
        </p>
        <ul
          className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            <span className="font-medium">Stage Position (0-25)</span> — Higher probability stages
            (e.g., Verbal Received at 90%) score more points than early stages (e.g., New at 5%).
          </li>
          <li>
            <span className="font-medium">Stage Velocity (0-25)</span> — Deals moving faster than
            the historical average for won deals score higher. Stalled deals score lower.
          </li>
          <li>
            <span className="font-medium">Close Date Health (0-25)</span> — Deals with a future
            close date and no slippage score highest. Repeated close date pushes reduce the score
            significantly.
          </li>
          <li>
            <span className="font-medium">Deal Momentum (0-25)</span> — Forward stage movement earns
            points. Stage regressions (going backward) reduce the score.
          </li>
        </ul>
      </section>
      <Separator />

      <section id="account-health" aria-labelledby="account-health-heading">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-4 w-4" aria-hidden="true" />
          <h3 id="account-health-heading" className="font-semibold">
            Account Health Score
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Every account gets a health score from 0 to 100 that flags accounts needing attention. The
          score appears as a color-coded badge in the <span className="font-medium">Health</span>{' '}
          column on the Accounts list, and as a detailed breakdown card on the Account detail page.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">How it is calculated:</span> The score combines four equally
          weighted signals (25 points each):
        </p>
        <ul
          className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            <span className="font-medium">Engagement (0-25)</span> — When was the last task or event
            on this account? Activity within 7 days earns full points. No activity in over 90 days
            scores zero.
          </li>
          <li>
            <span className="font-medium">Revenue Health (0-25)</span> — Based on total won revenue,
            whether there is open pipeline, and how recently the last deal was closed.
          </li>
          <li>
            <span className="font-medium">Renewal Risk (0-25)</span> — Monitors upcoming contract
            end dates. Accounts with renewals more than 90 days out score highest. Approaching
            expirations without a renewal opportunity score low.
          </li>
          <li>
            <span className="font-medium">Relationship Breadth (0-25)</span> — A mix of contact
            count, opportunity count, and recent activity volume. Broader engagement means lower
            churn risk.
          </li>
        </ul>
      </section>
      <Separator />

      <section id="next-best-action" aria-labelledby="next-best-action-heading">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-4 w-4" aria-hidden="true" />
          <h3 id="next-best-action-heading" className="font-semibold">
            Next Best Action
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          When an opportunity, lead, or account has a score below 60, the system suggests a single
          concrete action based on the weakest signal in the breakdown. The suggestion appears as a
          colored banner below the score card on detail pages.
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            <span className="font-medium">Red banner</span> — Critical signal (score under 5). Act
            immediately.
          </li>
          <li>
            <span className="font-medium">Yellow banner</span> — Weak signal (score 5-14). Address
            soon.
          </li>
          <li>
            <span className="font-medium">Blue banner</span> — Moderate signal (score 15+). Good to
            improve.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground">
          Healthy records (score 60+) do not show a suggestion, keeping the interface clean when no
          action is needed.
        </p>
      </section>
      <Separator />

      <ProTip>
        <span className="font-medium">Weekly review:</span> Pull up your Pipeline Report and
        Win/Loss Analysis before each team meeting. Knowing your numbers builds credibility and
        helps you ask for the support you need. Sort your opportunities by Deal Health Score and
        leads by Lead Score to quickly identify where to focus. Check the Next Best Action
        suggestions to know exactly what to do first.
      </ProTip>
    </>
  )
}
